import styled, { css } from 'styled-components'
import { noTouch } from '@/assets/styles/mixins.css.js'

const Select = styled.div`
  position: relative;
  display: inline-block;
  vertical-align: top;
  width: 100%;
  z-index: 1;

  ${props =>
    props.open &&
    css`
      z-index: 2;
    `}

  ${props =>
    props.selectType === 'form' &&
    css`
      --head-default-color: var(--color-extra);
      --head-open-color: var(--color-main);
      --head-selected-color: var(--color-main);
      --arrow-open-color: var(--color-decor);
      --arrow-selected-color: var(--color-decor);
      --arrow-position-right: 8px;
      --arrow-position-top: 13px;
      --underline-default-color: var(--color-border);
      --underline-open-color: var(--color-decor);
      --underline-selected-color: var(--color-decor);
      min-height: 40px;

      ${Select.Head} {
        padding: 6px 30px 16px 0;
        font-size: 16px;
        font-weight: normal;
        line-height: 1.2;
        border: none;

        &::before {
          content: '';
          position: absolute;
          top: 100%;
          left: 0;
          display: block;
          width: 100%;
          height: 2px;
          pointer-events: none;
          transition: background-color 0.3s ease;
        }
      }

      ${Select.Content} {
        max-height: 240px;
      }

      ${Select.RadioTitle} {
        padding: 11px 24px 10px;

        ${Select.Item}:first-child & {
          padding-top: 20px;
        }

        ${Select.Item}:last-child & {
          padding-bottom: 24px;
        }
      }
    `}
`

Select.Head = styled.div`
  position: relative;
  z-index: 2;
  padding: 14px 45px 15px 20px;
  font-weight: 500;
  font-size: 16px;
  line-height:24px;
  color: var(--head-default-color, var(--color-decor));
  user-select: none;
  cursor: pointer;
  text-align: left;
  background: #fff;
  border: 1px solid rgba(var(--color-border-rgb), 0.5);
  border-radius: 1px;
  transition: border-color 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    top: var(--arrow-position-top, 20px);
    right: var(--arrow-position-right, 16px);
    display: block;
    width:10px;
    height:16px;
    background-image: url("data:image/svg+xml,%3Csvg width='10' height='16' viewBox='0 0 10 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1.25974 0.999995L8.51948 8.25974L1.25974 15.5195' stroke='%23B69B64'/%3E%3C/svg%3E ");
    background-position:center;
    background-repeat:no-repeat;
    transform-origin: 50% 50%;
    transition: transform 0.3s ease-out;
  }

  &::before {
    background-color: var(--underline-default-color);
  }

  ${noTouch()`
    &:hover {
      border-color: var(--color-border);
    }
  `}

  ${props =>
    props.open &&
    css`
      color: var(--head-open-color, var(--color-decor));

      &::after {
        border-top-color: var(--arrow-open-color, currentColor);
        transform: rotate(90deg);
      }

      &::before {
        background-color: var(--underline-open-color);
      }
    `}

  ${props =>
    props.selected &&
    css`
      color: var(--head-selected-color, var(--color-decor));
      &::after {
        border-top-color: var(--arrow-selected-color, currentColor);
      }
      &::before {
        background-color: var(--underline-selected-color);
      }
    `}

  ${props =>
    props.disabled &&
    css`
      color: var(--head-disabled-color, var(--color-border));
      pointer-events: none;
    `}
`

Select.Body = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 2;
  width: ${props => (props.size ? `${props.size}px` : `100%`)};
  background: #fff;
  box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.08);
  transform: translateY(15px);
  outline: 1px solid #fff;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  overflow: hidden;
  transition: visibility 0s 0.3s, opacity 0.3s ease-out, transform 0.3s ease-out,
    border-color 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 0;
    display: block;
    width: 100%;
    height: 8px;
    background: 0 0;
  }

  ${props =>
    props.visible &&
    css`
      opacity: 1;
      transform: translateY(0);
      visibility: visible;
      pointer-events: all;
      transition: visibility 0s, opacity 0.3s ease-out, transform 0.3s ease-out,
        border-color 0.3s ease;
    `}
`

Select.Content = styled.div`
  max-height: 400px;
  overflow-y: auto;
  scrollbar-color: var(--color-extra) #fff;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    background-color: #fff;
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--color-extra);
  }
`

Select.Item = styled.div`
  ${props =>
    props.isChild &&
    css`
      ${Select.RadioTitle} {
        padding-left: 40px;
      }
    `}

  ${props =>
    props.disabled &&
    css`
      ${Select.RadioTitle} {
        &:hover {
          cursor: default;
          color: inherit;
        }
      }
    `}
`

Select.Radio = styled.label`
  display: block;
  width: 100%;
`

Select.RadioTitle = styled.span`
  display: block;
  width: 100%;
  padding: 12px 16px 12px 24px;
  font-size: 14px;
  cursor: pointer;
  transition: color 0.3s ease;

  ${Select.Item}:first-child & {
    padding-top: 24px;
  }

  ${Select.Item}:last-child & {
    padding-bottom: 26px;
  }

  ${noTouch()`
    &:hover {
      color: var(--color-decor);
    }
  `}
`

Select.RadioInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  border: 0;
  padding: 0;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  overflow: hidden;
  pointer-events: none;
  white-space: nowrap;

  ${props =>
    props.checked &&
    css`
      & + ${Select.RadioTitle} {
        font-weight: 500;
        color: var(--color-decor);
        cursor: default;
        pointer-events: none;
      }
    `}

  ${props =>
    props.checked && props.arrow &&
    css`
      & + ${Select.RadioTitle} {
        &:after {
          content:'';
          display:inline-block;
          width:21px;
          height:27px;
          background-image: url("data:image/svg+xml,%3Csvg width='21' height='9' viewBox='0 0 21 9' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20.3536 4.85355C20.5488 4.65829 20.5488 4.34171 20.3536 4.14645L17.1716 0.964466C16.9763 0.769204 16.6597 0.769204 16.4645 0.964466C16.2692 1.15973 16.2692 1.47631 16.4645 1.67157L19.2929 4.5L16.4645 7.32843C16.2692 7.52369 16.2692 7.84027 16.4645 8.03553C16.6597 8.2308 16.9763 8.2308 17.1716 8.03553L20.3536 4.85355ZM0 5H20V4H0V5Z' fill='%23B69B64'/%3E%3C/svg%3E%0A");
          background-repeat:no-repeat;
          background-position:center;
          margin-left:10px;
        }
      }
    `}
`

export default Select
